import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@mui/material";

import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";

/**
 * Support-session landing page (tenant_authentication_plan.md §2.5). A super
 * admin arrives here on the TENANT's subdomain with a single-use code minted
 * by Tenant Management; the code is redeemed for a tenant-scoped sign-in and
 * the app continues as the tenant's support user. The code dies on first use,
 * so a reload lands on the error state by design — the super admin opens a
 * fresh session from Tenant Management instead.
 */
const SupportSessionView = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { signInWithSupportSession } = FirebaseAuthContainer.useContainer();
    const [error, setError] = useState<string>("");
    // The effect must run exactly once per code: React 18 dev double-invokes
    // effects, and a second redeem of a single-use code always fails.
    const attempted = useRef<string | null>(null);

    useEffect(() => {
        const code = searchParams.get("code");
        if (!code) {
            setError("This link is missing its support-session code.");
            return;
        }
        if (attempted.current === code) return;
        attempted.current = code;
        // signInWithSupportSession clears any session already in this
        // browser before it redeems the code.
        signInWithSupportSession(code)
            .then(() => navigate("/", { replace: true }))
            .catch(err => {
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    "The support session could not be started.";
                setError(message);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    if (error) {
        return (
            <AuthTemplate>
                <div className="support-session-view" role="alert">
                    <h1 className="support-session-view__title">Support session unavailable</h1>
                    <p className="support-session-view__text">{error}</p>
                    <p className="support-session-view__text">
                        Support links work once and expire after two minutes. Open a new one from
                        Tenant Management.
                    </p>
                    <Button variant="contained" onClick={() => window.close()}>
                        Close
                    </Button>
                </div>
            </AuthTemplate>
        );
    }

    return (
        <AuthTemplate>
            <div className="support-session-view">
                <p className="support-session-view__text">Starting support session…</p>
                <LoaderView show={true} />
            </div>
        </AuthTemplate>
    );
};

export default SupportSessionView;
